import { useCallback, useEffect, useState, type KeyboardEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { BookOpen, CircleHelp, ClipboardList, Megaphone, Pencil } from "lucide-react";
import { Dialog } from "@/components/modals";
import { useAuth } from "@/contexts/AuthContext";
import { useNotification } from "@/contexts/NotificationContext";
import LoadingOverlay from "@/components/overlays/LoadingOverlay";
import CourseAnnounceDialog, { type AnnounceTarget } from "@/components/courses/CourseAnnounceDialog";
import { api, errorMessage } from "@/lib/api";
import { accessDeniedNavigateState } from "@/utils/accessDenied";
import "./Teach.css";

type InstructorPerms = {
  proposeActivities: boolean;
  editActivities: boolean;
  gradeActivities: boolean;
  overrideActivities: boolean;
  studentProgressChat?: boolean;
};

type ClassRow = {
  id: string;
  title: string;
  description?: string | null;
  schoolId: string;
  schoolName?: string | null;
  learnerCount: number;
  writeMode?: "edit" | "propose" | "none";
  perms?: InstructorPerms;
  canOpenOutline?: boolean;
  canViewWork?: boolean;
  canGradeWork?: boolean;
  canOverrideWork?: boolean;
  pendingCount?: number | null;
};

function learnerLabel(n: number) {
  return n === 1 ? "1 student" : `${n} students`;
}

export default function Teach() {
  const navigate = useNavigate();
  const { capabilities, pageAccess, loading: authLoading } = useAuth();
  const { notice } = useNotification();
  const canOpen = Boolean(
    pageAccess?.teach || capabilities?.teachEnrolled || capabilities?.teachWithoutEnrollment,
  );
  const canAnnounce = Boolean(capabilities?.postAnnouncements);
  const canBuild = Boolean(pageAccess?.["course-builder"]);

  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [announceTarget, setAnnounceTarget] = useState<AnnounceTarget | null>(null);
  const [aboutOpen, setAboutOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const envelope = await api<{ success: boolean; data: ClassRow[] }>("/api/teaching/classes");
      setClasses(Array.isArray(envelope.data) ? envelope.data : []);
    } catch (err) {
      notice({
        type: "error",
        title: "Could not load Teach",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setLoading(false);
    }
  }, [notice]);

  useEffect(() => {
    if (authLoading || !canOpen) return;
    void load();
  }, [authLoading, canOpen, load]);

  if (authLoading) {
    return <LoadingOverlay isVisible message="Loading…" />;
  }

  if (!canOpen) {
    return <Navigate to="/" replace state={accessDeniedNavigateState("teach")} />;
  }

  const openClass = (row: ClassRow) => {
    navigate(`/teach/${encodeURIComponent(row.id)}`);
  };

  const openOutline = (row: ClassRow) => {
    navigate(`/courses/${encodeURIComponent(row.id)}/build`);
  };

  const onCardKey = (event: KeyboardEvent<HTMLElement>, row: ClassRow) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openClass(row);
    }
  };

  return (
    <div className="al-classes">
      <LoadingOverlay isVisible={loading} message="Loading Teach…" />
      <header className="al-classes-header">
        <div>
          <div className="al-classes-title-row">
            <h1>Teach</h1>
            <button
              type="button"
              className="al-classes-help"
              aria-label="About Teach"
              title="About Teach"
              onClick={() => setAboutOpen(true)}
            >
              <CircleHelp size={16} aria-hidden="true" />
            </button>
          </div>
          <p className="al-classes-sub">
            Courses you instruct. Grade and outline stay on the card — click the
            card to open the class.
          </p>
        </div>
      </header>

      {!loading && classes.length === 0 ? (
        <div className="al-classes-empty">
          <BookOpen aria-hidden="true" />
          <h2>Nothing to teach yet</h2>
          <p>
            An admin adds you as an instructor on a school course (Courses → People),
            or Teach Without Enrollment lists other school copies in scope. Until then
            there is nothing here.
          </p>
        </div>
      ) : (
        <div className="al-classes-grid">
          {classes.map((row) => {
            const showOutline = Boolean(row.canOpenOutline && canBuild);
            const outlineLabel = row.writeMode === "propose" ? "Propose" : "Edit";
            const showGrade = Boolean(row.canViewWork || row.canGradeWork);
            const gradeLabel = row.canGradeWork ? "Grade" : "Submissions";
            return (
              <article
                key={row.id}
                className="al-classes-card al-classes-card--clickable"
                role="link"
                tabIndex={0}
                onClick={() => openClass(row)}
                onKeyDown={(event) => onCardKey(event, row)}
              >
                <div className="al-classes-card-top">
                  <div className="al-classes-card-icon">
                    <BookOpen aria-hidden="true" size={18} />
                  </div>
                  {canAnnounce ? (
                    <div
                      className="al-classes-card-actions"
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="al-classes-announce"
                        aria-label={`Announce in ${row.title}`}
                        title="Post a note students will see on Today"
                        onClick={() => setAnnounceTarget({ courseId: row.id, title: row.title })}
                      >
                        <Megaphone size={13} aria-hidden="true" />
                        Announce
                      </button>
                    </div>
                  ) : null}
                </div>
                <h2 className="al-classes-card-title">{row.title}</h2>
                <p className="al-classes-card-meta">
                  {row.schoolName || "School"} · {learnerLabel(row.learnerCount)}
                </p>
                <p className="al-classes-card-desc">
                  {row.description || <span className="al-classes-card-desc--empty">No description</span>}
                </p>
                {showOutline || showGrade ? (
                  <div
                    className="al-classes-card-verbs"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    {showOutline ? (
                      <button
                        type="button"
                        className="al-classes-verb"
                        onClick={() => openOutline(row)}
                      >
                        <Pencil size={12} aria-hidden="true" />
                        {outlineLabel}
                      </button>
                    ) : null}
                    {showGrade ? (
                      <button
                        type="button"
                        className="al-classes-verb"
                        onClick={() => navigate(`/teach/${encodeURIComponent(row.id)}#work`)}
                      >
                        <ClipboardList size={12} aria-hidden="true" />
                        {gradeLabel}
                        {row.pendingCount
                          ? ` · ${row.pendingCount} required`
                          : ""}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      <CourseAnnounceDialog target={announceTarget} onClose={() => setAnnounceTarget(null)} />
      <Dialog open={aboutOpen} title="About Teach" width="26rem" onClose={() => setAboutOpen(false)}>
        <div className="al-hint-copy">
          <p>
            These are school courses you instruct — not the school catalog (that is
            Courses). Click a card to open it. Grade and Propose or Edit stay on the
            card for the verbs you hold on that copy.
          </p>
          <p>
            Instructors use the enrollment kit on that copy (Propose, Edit, Grade,
            Override, student chat). Supervisors use Teach Without Enrollment on
            every in-scope copy, with its own kit — not propose or edit. If you are
            both, the enrollment kit wins. Viewing and marking papers for a teacher
            is the Grade verb on that enrollment; extra attempts are Override.
          </p>
        </div>
      </Dialog>
    </div>
  );
}
